import React, { useMemo } from 'react';
import { Button, Drawer, Empty, List, Space, Spin, Typography } from 'antd';
import { ClockCircleOutlined, FileTextOutlined, LinkOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { useEodStageLog } from '@/hooks/useEodClose';
import { parseWorkLogLines } from '@/utils/eodClose';

const { Text } = Typography;

type Props = {
  open: boolean;
  onClose: () => void;
  stageId: string | null;
  title: string;
  fallbackNotes: string | null;
  fallbackHours: number | null;
  workOrderId?: string | null;
};

export const EodOperationWorkLogDrawer: React.FC<Props> = ({
  open,
  onClose,
  stageId,
  title,
  fallbackNotes,
  fallbackHours,
  workOrderId,
}) => {
  const { data, isFetching } = useEodStageLog(stageId, open && !!stageId);
  const notes = data?.notes ?? fallbackNotes;
  const hours = data?.actual_hours ?? fallbackHours;
  const lines = useMemo(() => {
    const parsed = parseWorkLogLines(notes);
    return [...parsed].reverse();
  }, [notes]);

  return (
    <Drawer
      title="Operation log"
      open={open}
      onClose={onClose}
      width={520}
      destroyOnClose
      extra={
        workOrderId ? (
          <Link to={`/mfg/work-orders/${workOrderId}`}>
            <Button type="link" icon={<LinkOutlined />} size="small">
              Open work order
            </Button>
          </Link>
        ) : null
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Text type="secondary">{title}</Text>
        {hours != null ? (
          <Text strong>Actual hours (total): {hours}</Text>
        ) : null}
        {isFetching ? (
          <div style={{ textAlign: 'center', padding: 24 }}>
            <Spin />
          </div>
        ) : lines.length === 0 ? (
          <Empty description="No log entries yet for this operation." />
        ) : (
          <List
            size="small"
            dataSource={lines}
            renderItem={(line) => {
              const isEod = line.includes('[EOD_');
              return (
                <List.Item>
                  <List.Item.Meta
                    avatar={
                      isEod ? (
                        <ClockCircleOutlined style={{ color: '#8c8c8c' }} />
                      ) : (
                        <FileTextOutlined style={{ color: '#8c8c8c' }} />
                      )
                    }
                    title={<Text style={{ whiteSpace: 'pre-wrap' }}>{line}</Text>}
                  />
                </List.Item>
              );
            }}
          />
        )}
        <Button onClick={onClose} block>
          Close
        </Button>
      </Space>
    </Drawer>
  );
};

export default EodOperationWorkLogDrawer;
